import { useCallback, useEffect, useRef, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import { Banknote, Link2, Loader2, RefreshCw, Unlink, KeySquare, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

/**
 * ConnectBankButton
 * - Fetches a link_token from /api/plaid/create-link-token (lazy).
 * - Opens Plaid Link; on success exchanges the public_token, then syncs recurring
 *   transactions into the user's subscriptions via /api/plaid/sync.
 * - If already linked, the button becomes a "Sync now" button and exposes a
 *   secondary "Disconnect" action via onDisconnect.
 *
 * Props:
 *  - onLinked() — called once Plaid Link exchange succeeds
 *  - onSynced(result) — called after /plaid/sync returns; result = {imported, updated, total_streams, subscriptions}
 */
export default function ConnectBankButton({ status, onLinked, onSynced, onDisconnect }) {
  // Single-hook design: one usePlaidLink instance, one active token. The
  // `mode` flag remembers whether the loaded token is the normal search flow
  // or the manual (auth + micro-deposits) flow so the button that triggered
  // the load knows when to auto-open once Plaid Link is ready.
  const [linkToken, setLinkToken] = useState(null);
  const [mode, setMode] = useState("idle"); // 'idle' | 'normal' | 'manual'
  const [pendingOpen, setPendingOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [manualLoading, setManualLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [addingAnother, setAddingAnother] = useState(false);
  const linked = !!status?.linked;
  const atBankLimit =
    status && typeof status.item_count === "number" && typeof status.max_banks === "number"
      && status.item_count >= status.max_banks;
  // Monotonic request id so stale token responses can't clobber the most
  // recent user-initiated flow (e.g. manual response arriving after primary
  // click). Only the response whose id === latestRequest.current wins.
  const latestRequest = useRef(0);

  const fetchLinkToken = useCallback(async ({ manual = false } = {}) => {
    const setBusy = manual ? setManualLoading : setLoading;
    const reqId = ++latestRequest.current;
    try {
      setBusy(true);
      const url = manual
        ? "/plaid/create-link-token?manual=true"
        : "/plaid/create-link-token";
      const res = await api.post(url);
      // Stale response — a newer flow has already taken over. Drop this one.
      if (reqId !== latestRequest.current) return null;
      setLinkToken(res.data.link_token);
      setMode(manual ? "manual" : "normal");
      // OAuth banks redirect the browser away mid-Link; the same link_token
      // must be reused on return (see /plaid-oauth page). Only cache the
      // normal-flow token — the manual token is single-use.
      if (!manual) {
        try { localStorage.setItem("mmh_plaid_link_token", res.data.link_token); } catch { /* ignore */ }
      }
      return res.data.link_token;
    } catch (e) {
      if (reqId !== latestRequest.current) return null; // ignore stale error
      console.error(e);
      toast.error(
        manual
          ? "Couldn't start manual link. Try again in a moment."
          : "Couldn't start Plaid. Try again in a moment."
      );
      return null;
    } finally {
      if (reqId === latestRequest.current) setBusy(false);
    }
  }, []);

  // Pre-fetch a link token the first time the user is not yet linked so that the
  // Plaid modal opens instantly on click. Also prefetch when linked so the "Add
  // another bank" button opens without a token-fetch delay.
  useEffect(() => {
    if (!linkToken && !atBankLimit) fetchLinkToken({ manual: false });
  }, [linkToken, atBankLimit, fetchLinkToken]);

  const runSync = useCallback(async ({ silent = false } = {}) => {
    setSyncing(true);
    try {
      const res = await api.post("/plaid/sync");
      const { imported, updated, total_streams } = res.data;
      if (!silent) {
        toast.success(
          imported + updated > 0
            ? `Synced ${imported + updated} recurring charge${imported + updated === 1 ? "" : "s"}`
            : "All caught up · no new recurring charges",
          {
            description:
              imported > 0
                ? `${imported} new · ${updated} refreshed · ${total_streams} total streams`
                : `${total_streams} streams scanned`,
          }
        );
      }
      onSynced?.(res.data);
    } catch (e) {
      console.error(e);
      const detail = e.response?.data?.detail;
      const code = detail?.code;
      if (e.response?.status === 202 || code === "PRODUCT_NOT_READY") {
        toast.message("Still indexing your bank…", {
          description:
            detail?.message ||
            "Plaid is preparing your recurring transactions. Tap Sync again in ~30 seconds.",
          duration: 6000,
        });
      } else {
        toast.error("Sync failed. Please try again.");
      }
    } finally {
      setSyncing(false);
    }
  }, [onSynced]);

  const onPlaidSuccess = useCallback(async (public_token) => {
    try {
      setSyncing(true);
      await api.post("/plaid/exchange-public-token", { public_token });
      try { localStorage.removeItem("mmh_plaid_link_token"); } catch { /* ignore */ }
      onLinked?.();
      toast.success("Bank linked!", {
        description: "Scanning for recurring charges…",
      });
      // Immediately attempt a sync; if Plaid is not ready, we'll surface the
      // friendly PRODUCT_NOT_READY toast.
      await runSync({ silent: false });
    } catch (e) {
      console.error(e);
      toast.error("Could not connect bank. Please try again.");
    } finally {
      setSyncing(false);
    }
  }, [onLinked, runSync]);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: (err) => {
      if (err) {
        console.warn("Plaid exit with error", err);
      }
    },
  });

  const handleManualClick = async () => {
    // Explain the flow before opening — micro-deposit verification takes 1-3
    // business days, so we don't want users to think the link is instant.
    toast.info("Manual bank linking", {
      description:
        "You'll enter your bank's routing + account number. Plaid then sends 2 tiny deposits (~$0.01 each) to your account. Come back in 1-3 business days to verify. Perfect for Cash App, Chime, Varo, or any bank Plaid can't find directly.",
      duration: 11000,
    });
    setPendingOpen(true);
    await fetchLinkToken({ manual: true });
    // Auto-open handled by the useEffect below when ready flips true after the
    // manual token swaps in.
  };

  // Auto-open Plaid Link once (a) the user has requested a manual/normal flow
  // and (b) Plaid Link has finished initializing with the loaded token.
  useEffect(() => {
    if (pendingOpen && linkToken && ready && !syncing) {
      open();
      setPendingOpen(false);
      setAddingAnother(false);
    }
  }, [pendingOpen, linkToken, ready, syncing, open]);

  const handleAddAnother = async ({ manual = false } = {}) => {
    if (atBankLimit) {
      toast.error("Bank limit reached", {
        description: `Your tier supports ${status?.max_banks} bank${status?.max_banks === 1 ? "" : "s"}. Upgrade to add more.`,
      });
      return;
    }
    setAddingAnother(true);
    setPendingOpen(true);
    if (manual) {
      toast.info("Manual bank linking", {
        description:
          "You'll enter your bank's routing + account number. Plaid then sends 2 tiny deposits (~$0.01 each) to your account. Come back in 1-3 business days to verify.",
        duration: 8000,
      });
    }
    await fetchLinkToken({ manual });
  };

  const handleDisconnect = async () => {
    try {
      setSyncing(true);
      const res = await api.delete("/plaid/disconnect");
      toast.success("Bank disconnected", {
        description: res.data?.removed_subs > 0
          ? `Removed ${res.data.removed_subs} auto-imported subscription${res.data.removed_subs === 1 ? "" : "s"}.`
          : "Manual subscriptions kept.",
      });
      onDisconnect?.();
    } catch (e) {
      console.error(e);
      toast.error("Couldn't disconnect. Try again.");
    } finally {
      setSyncing(false);
    }
  };

  // Not configured: backend returned configured:false
  if (status && !status.configured) {
    return null;
  }

  if (linked) {
    return (
      <div className="flex items-center gap-2 flex-wrap" data-testid="plaid-linked-actions">
        <Button
          size="sm"
          onClick={() => runSync()}
          disabled={syncing}
          className="h-9 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-extrabold gap-1.5 px-3 disabled:opacity-70"
          data-testid="plaid-sync-button"
        >
          {syncing ? (
            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.8} />
          ) : (
            <RefreshCw className="w-4 h-4" strokeWidth={2.8} />
          )}
          Sync
        </Button>
        <Button
          size="sm"
          onClick={() => {
            if (atBankLimit) {
              toast.error("Bank limit reached", {
                description: `Your tier supports ${status?.max_banks} bank${status?.max_banks === 1 ? "" : "s"}. Upgrade to add more.`,
              });
              return;
            }
            handleAddAnother({ manual: false });
          }}
          disabled={syncing || addingAnother}
          className={`h-9 rounded-xl bg-slate-900 hover:bg-slate-800 text-amber-400 font-extrabold gap-1.5 px-3 disabled:opacity-70 ${
            atBankLimit ? "opacity-60" : ""
          }`}
          data-testid="plaid-add-another-button"
          aria-disabled={atBankLimit || undefined}
          title={atBankLimit ? "Bank limit reached — upgrade to add more" : "Link another bank"}
        >
          {addingAnother ? (
            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.8} />
          ) : (
            <Plus className="w-4 h-4" strokeWidth={2.8} />
          )}
          Add
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleDisconnect}
          disabled={syncing}
          className="h-9 rounded-xl text-slate-500 hover:text-red-600 hover:bg-red-50 font-bold gap-1.5 px-2"
          data-testid="plaid-disconnect-button"
          aria-label="Disconnect bank"
        >
          <Unlink className="w-4 h-4" strokeWidth={2.6} />
        </Button>
        {status?.item_count > 0 && status?.max_banks && (
          <span
            className="text-[10px] font-black uppercase tracking-wider text-slate-500 pl-1"
            data-testid="bank-count-chip"
          >
            {status.item_count}/{status.max_banks} banks
          </span>
        )}
      </div>
    );
  }

  const HINT_KEY = "mmh_plaid_hint_shown_v2";

  const handleConnectClick = async () => {
    // First-time hint: Plaid Link's optional phone-verification step rejects
    // many real US numbers in sandbox + any VOIP/landline in prod. Make sure
    // users know they can skip it.
    let alreadyShown = false;
    try { alreadyShown = sessionStorage.getItem(HINT_KEY) === "1"; } catch { /* ignore */ }
    if (!alreadyShown) {
      toast.info("Heads up about Plaid", {
        description:
          "• If Plaid asks for your phone, tap 'Continue without phone number' — it's optional.\n" +
          "• Using Cash App? Search 'Lincoln Savings Bank' (Cash App's sponsor bank on Plaid) — Cash App itself won't appear in the list.",
        duration: 9000,
      });
      try { sessionStorage.setItem(HINT_KEY, "1"); } catch { /* ignore */ }
    }
    // If we already have the normal token loaded and Plaid is ready, open now.
    if (mode === "normal" && linkToken && ready) {
      open();
      return;
    }
    // Otherwise fetch the normal token and let the useEffect auto-open it once
    // Plaid Link finishes re-initializing.
    setPendingOpen(true);
    await fetchLinkToken({ manual: false });
  };

  return (
    <div className="flex flex-col items-stretch gap-1.5" data-testid="plaid-connect-actions">
      <Button
        size="sm"
        onClick={handleConnectClick}
        disabled={loading || syncing}
        className="h-9 rounded-xl bg-slate-900 hover:bg-slate-800 text-amber-400 font-extrabold gap-1.5 px-3 disabled:opacity-70"
        data-testid="plaid-connect-button"
      >
        {loading || syncing ? (
          <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.8} />
        ) : (
          <Link2 className="w-4 h-4" strokeWidth={2.8} />
        )}
        <span className="inline-flex items-center gap-1">
          <Banknote className="w-4 h-4" strokeWidth={2.6} />
          Connect bank
        </span>
      </Button>
      <button
        type="button"
        onClick={handleManualClick}
        disabled={manualLoading || syncing}
        className="text-[11px] text-slate-500 hover:text-slate-900 underline underline-offset-2 inline-flex items-center gap-1 justify-center disabled:opacity-60"
        data-testid="plaid-manual-connect-button"
      >
        {manualLoading ? (
          <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2.8} />
        ) : (
          <KeySquare className="w-3 h-3" strokeWidth={2.6} />
        )}
        Can&rsquo;t find your bank? Link with routing #
      </button>
    </div>
  );
}
